/**
 * Pure helpers for deciding which outgoing URLs should bypass the Rust
 * proxy. Kept in a standalone module (no atom/store imports, no heavy
 * transitive deps) so they can be imported from tests without pulling
 * `execute-request` + the document-list + docsapi chain into the module
 * graph at load time. Tests that touch this file stay hermetic.
 */

export const isTauriIpcUrl = (url: string): boolean =>
	url.startsWith("ipc://") || url.startsWith("http://ipc.localhost");

/**
 * URLs that should bypass the Rust proxy and use the browser's native fetch.
 *
 * Categories:
 * - Tauri IPC / non-HTTP — Rust can't execute these.
 * - AWS (`*.amazonaws.com`) — handled by the sibling AWS interceptor for SigV4.
 * - Local docsapi dev (`http://localhost:3333`) — already same-origin, no CORS.
 * - Reboot collab (`*.prod1.rbt.cloud:9991`) — websocket/gRPC traffic, not HTTP.
 * - CDN imports (skypack / esm.sh / jspm / unpkg / jsdelivr / esm.run) —
 *   need the browser's native fetch so dynamic `import()` works.
 */
export const shouldBypassProxy = (url: string): boolean => {
	if (isTauriIpcUrl(url)) {
		return true;
	}

	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return true;
	}

	if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
		return true;
	}

	if (parsed.hostname.endsWith(".amazonaws.com")) {
		return true;
	}

	if (parsed.protocol === "http:" && parsed.hostname === "localhost" && parsed.port === "3333") {
		return true;
	}

	if (parsed.host.endsWith(".prod1.rbt.cloud:9991")) {
		return true;
	}

	const cdnHosts = [
		"cdn.skypack.dev",
		"esm.sh",
		"jspm.dev",
		"unpkg.com",
		"cdn.jsdelivr.net",
		"esm.run",
	];
	if (cdnHosts.some((h) => parsed.hostname === h || parsed.hostname.endsWith(`.${h}`))) {
		return true;
	}

	return false;
};
